import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

/**
 * Limit detail type matching the backend response
 */
export interface LimitDetail {
  limitKey: string;
  limitType: string;
  currentUsage?: number;
  maxAllowed?: number;
  percentage?: number;
  remaining?: number;
  warningLevel: 'ok' | 'info' | 'warning' | 'critical' | 'blocked';
  message: string;
  unlimited?: boolean;
  enabled?: boolean;
  available?: boolean;
}

/**
 * Comprehensive limits status response from API
 */
export interface LimitsStatusResponse {
  teamId: string;
  planId: string;
  subscriptionStatus: string;
  limits: LimitDetail[];
  overallStatus: 'ok' | 'info' | 'warning' | 'critical' | 'blocked';
  hasAnyLimitIssues: boolean;
  criticalLimits: string[];
  summary: {
    total: number;
    ok: number;
    info: number;
    warning: number;
    critical: number;
    blocked: number;
  };
}

/**
 * Hook to fetch comprehensive limits status for a team
 * @param teamId - Team ID to fetch limits for
 * @returns React Query result with limits data
 */
export function useLimits(teamId: string | undefined) {
  return useQuery<LimitsStatusResponse>({
    queryKey: ['limits', 'status', { teamId }],
    queryFn: async () => {
      if (!teamId) {
        throw new Error('Team ID is required to fetch limits');
      }
      return apiClient<LimitsStatusResponse>('/api/limits/status');
    },
    enabled: !!teamId,
    staleTime: 60 * 1000, // Consider data fresh for 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

/**
 * Hook to fetch quick limits status (lightweight, for frequent polling)
 * @param teamId - Team ID to fetch limits for
 * @returns React Query result with quick limits data
 */
export function useQuickLimits(teamId: string | undefined) {
  return useQuery({
    queryKey: ['limits', 'quick-status', { teamId }],
    queryFn: async () => {
      if (!teamId) {
        throw new Error('Team ID is required to fetch limits');
      }
      return apiClient('/api/limits/quick-status');
    },
    enabled: !!teamId,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 2 * 60 * 1000, // Keep in cache for 2 minutes
    refetchInterval: 60 * 1000, // Refetch every minute for real-time updates
  });
}
